// CSV import for the CRM (spec 2026-11-03): parse → map columns → preview each row as New /
// Already in book / Problem. Pure; the dialog (ImportCsv.jsx) does the writes.
import { STATUS_META, PRIORITY_META, normalizeName, CLIENT_OWNER } from "./crmLogic.js";

export const CONTACT_FIELDS = ["name", "organization", "role", "base", "expertise", "email", "phone", "source"];
export const TRACK_FIELDS = ["priority", "status", "goal", "owner", "nextStep", "nextStepDate", "notes"];
export const FIELD_LABELS = {
  name: "Name", organization: "Organization", role: "Role", base: "Base", expertise: "Expertise", email: "Email",
  phone: "Phone", source: "How we met", priority: "Priority", status: "Status", goal: "Goal", owner: "Working it",
  nextStep: "Next step", nextStepDate: "Next step date", notes: "Notes",
};
const ALIASES = {
  name: ["fullname", "contact", "contactname"],
  organization: ["org", "agency", "unit", "company"],
  role: ["title", "position", "jobtitle"],
  base: ["location", "installation", "site"],
  expertise: ["tags", "focus", "focusarea", "expertiseareas", "techarea"],
  email: ["emailaddress", "mail"],
  phone: ["phonenumber", "cell", "mobile", "tel", "telephone"],
  source: ["source", "met", "howmet", "metat"],
  owner: ["workingit", "assignee", "assignedto"],
  nextStep: ["nextaction"],
  nextStepDate: ["followupdate", "nextdate", "duedate", "followup"],
  notes: ["note", "comments"],
};
const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// RFC 4180-ish: quoted fields with "" escapes, embedded commas and newlines; CRLF or LF; strips
// a leading BOM; drops rows whose cells are all blank.
export function parseCsv(text) {
  const src = String(text || "").replace(/^﻿/, "");
  const rows = [];
  let row = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') { cell += '"'; i++; } else quoted = false;
      } else cell += ch;
    } else if (ch === '"' && cell === "") quoted = true;
    else if (ch === ",") { row.push(cell); cell = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      row.push(cell); rows.push(row); row = []; cell = "";
    } else cell += ch;
  }
  if (cell !== "" || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
}

export function autoMap(headers, { withTrack = false } = {}) {
  const fields = withTrack ? [...CONTACT_FIELDS, ...TRACK_FIELDS] : CONTACT_FIELDS;
  const used = new Set();
  const out = {};
  (headers || []).forEach((h, i) => {
    const n = norm(h);
    const field = fields.find((f) => !used.has(f) && (norm(f) === n || norm(FIELD_LABELS[f]) === n || (ALIASES[f] || []).includes(n))) || null;
    if (field) used.add(field);
    out[i] = field;
  });
  return out;
}

export function templateCsv({ withTrack = false } = {}) {
  const fields = withTrack ? [...CONTACT_FIELDS, ...TRACK_FIELDS] : CONTACT_FIELDS;
  return fields.map((f) => FIELD_LABELS[f]).join(",") + "\n";
}

function parseDate(v) {
  const s = v.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (!m) return null;
  const iso = `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
  const d = new Date(`${iso}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === iso ? iso : null;
}
const keyFor = (meta, v) => {
  const n = norm(v);
  if (n === "medium") return "med";
  return Object.keys(meta).find((k) => norm(k) === n || norm(meta[k].label) === n) || null;
};
function matchOwner(users, v) {
  const n = v.trim().toLowerCase();
  const full = (u) => `${u.firstName || ""} ${u.lastName || ""}`.trim().toLowerCase();
  const byEmailOrName = (users || []).find((u) => (u.email || "").toLowerCase() === n || full(u) === n || (u.name || "").toLowerCase() === n);
  if (byEmailOrName) return byEmailOrName;
  const byFirst = (users || []).filter((u) => (u.firstName || "").toLowerCase() === n);
  return byFirst.length === 1 ? byFirst[0] : null;
}

// rows: data rows (header excluded). mapping: { [columnIndex]: field | null }.
export function buildPreview(rows, mapping, { contacts = [], users = [], clientTracks = [], withTrack = false } = {}) {
  const byEmail = new Map();
  for (const c of contacts) if (c.email) byEmail.set(String(c.email).trim().toLowerCase(), c.id);
  const onClient = new Set((clientTracks || []).map((t) => t.contactId));
  const seen = new Map(); // email → "row:<i>" for earlier rows in this sheet
  const seenNames = new Map(); // normalized name → "row:<i>", for earlier rows with no email
  const byName = new Map(); // normalized name → book contacts (no-email rows can only match by name)
  for (const c of contacts) { const n = normalizeName(c.name); if (n) byName.set(n, [...(byName.get(n) || []), c]); }

  return (rows || []).map((cells, index) => {
    const get = (field) => {
      const col = Object.keys(mapping).find((k) => mapping[k] === field);
      return col == null ? "" : String(cells[Number(col)] ?? "").trim();
    };
    const warnings = [];
    const contact = {};
    for (const f of CONTACT_FIELDS) contact[f] = get(f);
    contact.expertise = contact.expertise ? contact.expertise.split(/[;,]/).map((s) => s.trim()).filter(Boolean) : [];
    contact.email = contact.email ? contact.email.toLowerCase() : "";
    if (!contact.name) return { index, kind: "problem", contact, track: null, warnings, problem: "Name is missing — row skipped" };

    let kind = "new";
    let existingId;
    let sameSheet = false;
    if (contact.email && byEmail.has(contact.email)) { kind = "existing"; existingId = byEmail.get(contact.email); }
    else if (contact.email && seen.has(contact.email)) { kind = "existing"; existingId = seen.get(contact.email); sameSheet = true; }
    else if (contact.email) seen.set(contact.email, `row:${index}`);
    else {
      // No email: match on name so a business-card contact isn't added twice on re-import.
      const n = normalizeName(contact.name);
      const matches = byName.get(n) || [];
      if (matches.length === 1) { kind = "existing"; existingId = matches[0].id; warnings.push(`Matched by name (no email) — linked to “${matches[0].name}” already in the book`); }
      else if (matches.length > 1) warnings.push(`Similar name already in the book (${matches.length} contacts) — added as new; merge by hand if it's the same person`);
      else if (seenNames.has(n)) { kind = "existing"; existingId = seenNames.get(n); sameSheet = true; }
      else seenNames.set(n, `row:${index}`);
    }

    let track = null;
    if (withTrack) {
      if (sameSheet) warnings.push(`Same person as row ${Number(existingId.slice(4)) + 1} in this sheet — not added twice`);
      else if (kind === "existing" && onClient.has(existingId)) warnings.push("Already on this client — skipped");
      else {
        track = { status: "lead", priority: "med", goal: get("goal"), nextStep: get("nextStep"), nextStepDate: null, ownerUserId: null, note: get("notes") };
        const status = get("status"), priority = get("priority"), owner = get("owner"), date = get("nextStepDate");
        if (status) { const k = keyFor(STATUS_META, status); if (k) track.status = k; else warnings.push(`Unknown status "${status}" — set to Lead`); }
        if (priority) { const k = keyFor(PRIORITY_META, priority); if (k) track.priority = k; else warnings.push(`Unknown priority "${priority}" — set to Med`); }
        if (owner && norm(owner) === CLIENT_OWNER) track.ownerUserId = CLIENT_OWNER;
        else if (owner) { const u = matchOwner(users, owner); if (u) track.ownerUserId = u.id; else warnings.push(`No teammate matches "${owner}" — unassigned`); }
        if (date) { const d = parseDate(date); if (d) track.nextStepDate = d; else warnings.push(`Can't read date "${date}" — left blank`); }
      }
    }
    return { index, kind, contact, ...(existingId ? { existingId } : {}), track, warnings };
  });
}
